import {
    ReactElement,
    useEffect,
} from 'react';

import {
    SimpleGrid,
    Table,
    Text
} from '@mantine/core';

import {useApplications} from '../hooks/useApplications.ts';

import {
    RowSelectionDataCell,
    SimpleTableBatchAction,
    SimpleTablePagination,
    TableBody,
    TableBodyColumn,
    TableHeaderColumn,
    TableHeaderRow,
} from '@flexinet/ui-components';
import {useApplicationsSelection} from '../hooks/useApplicationsSelection.ts';
import {
    Application,
    IApplicationTableColumn,
} from '../types';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {useApplicationsPaging} from "../hooks/useApplicationsPaging.ts";
import {useApplicationsSearch} from "../hooks/useApplicationsSearch.ts";
import {I18n} from "@aws-amplify/core";
import {EnableRecordButton} from "../buttons/EnableRecordButton.tsx";
import {useAccountApplications} from "../../account-applications/hooks/useAccountApplications";
import {useUserAccount} from '../../../../hooks/useUserAccount.ts';
import {AccountApplication} from "../../account-applications/types";
import {DisableRecordButton} from '../buttons/DisableRecordButton.tsx';

export const RecordsTable = ({
                                 extraTools,
                                 settings,
                             }: {
    extraTools?: ReactElement[],
    settings?: { label: string, onClick: () => void }[]
}) => {

    const {allowedActions} = useVerifiedPermissions()


    const {
        visibleItems: allBusinessApplications,
        loading,
        fetchRecords,
    } = useApplications();

    const {
        limit,
        totalPages,
        totalCount,
        handleItemsPerPageChange,
        handlePageChange,
    } = useApplicationsPaging();

    const {
        handleFilterByKeyword,
    } = useApplicationsSearch();

    const {
        selection,
        handleRowSelection,
        handleSelectAllRows,
    } = useApplicationsSelection();

    useEffect(() => {
        fetchRecords();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const {userAccount} = useUserAccount()
    if (!userAccount) {
        throw new Error('User account not found');
    }

    const {
        visibleItems: enabledApplications,
        fetchRecords: fetchAccountApplications,
    } = useAccountApplications(userAccount.id);

    useEffect(() => {
        fetchAccountApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const findAccountApplication = (id: string) => enabledApplications?.find((enabledItem: AccountApplication) => enabledItem.applicationId === id);

    const columns = [
        {
            title: I18n.get('Name'),
            width: '1fr',
            render: (item: Application) => <Text tt={'uppercase'}>{item.name}</Text>,
        },
        {
            title: I18n.get('Description'),
            width: '1fr',
            render: (item: Application) => item.description,
        },
        {
            title: I18n.get('Enable'),
            width: '1fr',
            render: (item: Application) => {
                const accountApplication = findAccountApplication(item.id) as AccountApplication;

                return accountApplication ? <DisableRecordButton record={accountApplication}/> :
                    <EnableRecordButton record={item}/>
            }
        },
    ];


    const getColumns = () => {
        let cols: IApplicationTableColumn[] = [];
        if (allowedActions.includes('DeleteApplication')) {

            cols.push({
                          id: 'select',
                          title: '',
                          render: item => {
                              return <RowSelectionDataCell item={item} handleChange={handleRowSelection}/>;
                          },


                      },
            );
        }

        cols = cols.concat(columns);

        return cols;

    };


    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <SimpleTableBatchAction
                limit={limit}
                handleItemsPerPageChange={handleItemsPerPageChange}
                settings={settings}
                handleFilterByKeyword={handleFilterByKeyword}
                extraTools={extraTools}
            />
            <Table verticalSpacing='sm' striped highlightOnHover>
                <TableHeaderRow
                    columns={getColumns() as TableHeaderColumn[]}
                    handleSelectAllRows={handleSelectAllRows}
                />
                <TableBody
                    items={allBusinessApplications}
                    loading={loading}
                    selection={selection}
                    columns={getColumns() as TableBodyColumn[]}
                />
            </Table>

            <SimpleTablePagination
                totalPages={totalPages}
                visibleCount={totalCount}
                handlePageChange={handlePageChange}
            />
        </SimpleGrid>
    );
};
